import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Plus,
  Trash2
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import LocationAutocomplete from '@/components/common/LocationAutocomplete'
import SkillsSelector from '@/components/common/SkillsSelector'
import { useUserProfile } from '@/contexts/UserContext'

interface CreatePositionModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface SelectedSkill {
  id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
}

interface PositionData {
  title: string
  department: string
  companyName: string
  location: string
  type: 'full-time' | 'part-time' | 'contract'
  priority: 'high' | 'medium' | 'low'
  salary: {
    min: number
    max: number
    currency: string
  }
  deadline: string
  requirements: string[]
  description: string
  recruiter: string
  recruiterId: string
  isPublic: boolean
  skills: SelectedSkill[]
}

export default function CreatePositionModal({ open, onClose, onSuccess }: CreatePositionModalProps) {
  const userProfile = useUserProfile()
  const [formData, setFormData] = useState<PositionData>({
    title: '',
    department: '',
    companyName: '',
    location: '',
    type: 'full-time',
    priority: 'medium',
    salary: {
      min: 0,
      max: 0,
      currency: 'USD'
    },
    deadline: '',
    requirements: [''],
    description: '',
    recruiter: 'Sarah Wilson',
    recruiterId: 'rec_001',
    isPublic: true,
    skills: []
  })
  
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const canCreatePosition = userProfile?.recruiter_status === 'approved' || userProfile?.is_recruiter

  const departments = [
    'Engineering',
    'Product',
    'Design',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
    'Data',
    'Security'
  ]

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    
    // Only validate salary range if both values are provided
    if ((formData.salary.min > 0 || formData.salary.max > 0) && formData.salary.min >= formData.salary.max) {
      newErrors.salaryRange = 'Maximum salary must be greater than minimum salary'
    }
    
    // Only validate deadline if provided and check if it's not in the past
    if (formData.deadline) {
      const deadlineDate = new Date(formData.deadline)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (deadlineDate < today) {
        newErrors.deadline = 'Deadline cannot be in the past'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    if (!canCreatePosition) {
      setErrors({ submit: 'Your recruiter profile is pending approval. Please wait for admin approval before creating positions.' })
      return
    }
    
    setLoading(true)
    try {
      // Filter out empty requirements
      const cleanedData = {
        ...formData,
        company_name: formData.companyName.trim(),
        requirements: formData.requirements.filter(req => req.trim())
      }
      
      const response = await apiClient.createPosition(cleanedData)
      
      if (response.success) {
        setSubmitted(true)
        setTimeout(() => {
          onSuccess?.() // Call success callback to refresh data
          onClose()
          resetForm()
        }, 2000)
      } else {
        const errorMessage = response.error || 'Failed to create position'
        throw new Error(errorMessage)
      }
      
    } catch (error) {
      console.error('Failed to create position:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create position' })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      department: '',
      companyName: '',
      location: '',
      type: 'full-time',
      priority: 'medium',
      salary: {
        min: 0,
        max: 0,
        currency: 'USD'
      },
      deadline: '',
      requirements: [''],
      description: '',
      recruiter: 'Sarah Wilson',
      recruiterId: 'rec_001',
      isPublic: true,
      skills: []
    })
    setSubmitted(false)
    setErrors({})
  }

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }))
  }

  const removeRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }))
  }

  const updateRequirement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }))
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Create New Position</h2>
                    <p className="text-sm text-gray-500">Add a new job opening to your recruitment pipeline</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Position Created!</h3>
                    <p className="text-gray-600 mb-4">
                      The position "{formData.title}" has been successfully created.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-sm text-green-800">
                        <p><strong>Title:</strong> {formData.title}</p>
                        <p><strong>Company:</strong> {formData.companyName}</p>
                        <p><strong>Department:</strong> {formData.department}</p>
                        <p><strong>Location:</strong> {formData.location}</p>
                        <p><strong>Type:</strong> {formData.type}</p>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {!canCreatePosition && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-yellow-800">Recruiter approval required</p>
                          <p className="text-sm text-yellow-700">Your recruiter profile must be approved before creating positions.</p>
                        </div>
                      </div>
                    )}
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={formData.companyName}
                          onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                          placeholder="e.g. Acme Corp"
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.companyName ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.companyName && (
                          <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Position Title
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g. Senior Frontend Developer"
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.title ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.title && (
                          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Department
                        </label>
                        <select
                          value={formData.department}
                          onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.department ? 'border-red-300' : ''
                          }`}
                        >
                          <option value="" className="text-gray-900 bg-white">Select department</option>
                          {departments.map(dept => (
                            <option key={dept} value={dept} className="text-gray-900 bg-white">{dept}</option>
                          ))}
                        </select>
                        {errors.department && (
                          <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Location
                        </label>
                        <LocationAutocomplete
                          value={formData.location}
                          onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                          placeholder="Search for a city or type 'Remote'..."
                          error={errors.location}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Employment Type
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        >
                          <option value="full-time" className="text-gray-900 bg-white">Full-time</option>
                          <option value="part-time" className="text-gray-900 bg-white">Part-time</option>
                          <option value="contract" className="text-gray-900 bg-white">Contract</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority Level
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        >
                          <option value="low" className="text-gray-900 bg-white">Low</option>
                          <option value="medium" className="text-gray-900 bg-white">Medium</option>
                          <option value="high" className="text-gray-900 bg-white">High</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Application Deadline
                        </label>
                        <input
                          type="date"
                          value={formData.deadline}
                          onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.deadline ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.deadline && (
                          <p className="mt-1 text-sm text-red-600">{errors.deadline}</p>
                        )}
                      </div>
                    </div>

                    {/* Public Position Toggle */}
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center h-5">
                        <input
                          id="isPublic"
                          type="checkbox"
                          checked={formData.isPublic}
                          onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                      </div>
                      <div className="text-sm">
                        <label htmlFor="isPublic" className="font-medium text-gray-700 cursor-pointer">
                          Make this position public
                        </label>
                        <p className="text-gray-500">
                          When enabled, this position will be visible to job seekers on the public jobs page and can receive external applications.
                        </p>
                      </div>
                    </div>

                    {/* Salary Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Salary Range
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <input
                            type="number"
                            value={formData.salary.min || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              salary: { ...prev.salary, min: Number(e.target.value) }
                            }))}
                            placeholder="Minimum"
                            className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                              errors.salaryMin ? 'border-red-300' : ''
                            }`}
                          />
                          {errors.salaryMin && (
                            <p className="mt-1 text-sm text-red-600">{errors.salaryMin}</p>
                          )}
                        </div>
                        <div>
                          <input
                            type="number"
                            value={formData.salary.max || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              salary: { ...prev.salary, max: Number(e.target.value) }
                            }))}
                            placeholder="Maximum"
                            className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                              errors.salaryMax ? 'border-red-300' : ''
                            }`}
                          />
                          {errors.salaryMax && (
                            <p className="mt-1 text-sm text-red-600">{errors.salaryMax}</p>
                          )}
                        </div>
                        <div>
                          <select
                            value={formData.salary.currency}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              salary: { ...prev.salary, currency: e.target.value }
                            }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          >
                            <option value="USD" className="text-gray-900 bg-white">USD (US Dollar)</option>
                            <option value="EUR" className="text-gray-900 bg-white">EUR (Euro)</option>
                            <option value="GBP" className="text-gray-900 bg-white">GBP (British Pound)</option>
                            <option value="INR" className="text-gray-900 bg-white">INR (Indian Rs)</option>
                            <option value="CAD" className="text-gray-900 bg-white">CAD (Canadian Dollar)</option>
                            <option value="AUD" className="text-gray-900 bg-white">AUD (Australian Dollar)</option>
                            <option value="JPY" className="text-gray-900 bg-white">JPY (Japanese Yen)</option>
                            <option value="SGD" className="text-gray-900 bg-white">SGD (Singapore Dollar)</option>
                          </select>
                        </div>
                      </div>
                      {errors.salaryRange && (
                        <p className="mt-1 text-sm text-red-600">{errors.salaryRange}</p>
                      )}
                    </div>

                    {/* Requirements */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Requirements
                      </label>
                      <div className="space-y-2">
                        {formData.requirements.map((requirement, index) => (
                          <div key={index} className="flex space-x-2">
                            <input
                              type="text"
                              value={requirement}
                              onChange={(e) => updateRequirement(index, e.target.value)}
                              placeholder={`Requirement ${index + 1}`}
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                            />
                            {formData.requirements.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRequirement(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addRequirement}
                          className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Requirement</span>
                        </button>
                      </div>
                      {errors.requirements && (
                        <p className="mt-1 text-sm text-red-600">{errors.requirements}</p>
                      )}
                    </div>

                    {/* Job Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={6}
                        placeholder="Detailed job description, responsibilities, and qualifications..."
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                          errors.description ? 'border-red-300' : ''
                        }`}
                      />
                      {errors.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                      )}
                    </div>

                    {/* Skills Selection */}
                    <div>
                      <SkillsSelector
                        selectedSkills={formData.skills}
                        onSkillsChange={(skills) => setFormData(prev => ({ ...prev, skills }))}
                        placeholder="Search for required skills..."
                        error={errors.skills}
                      />
                    </div>

                    {/* Error Message */}
                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        <p className="text-sm text-red-800">{errors.submit}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !canCreatePosition}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        <span>{loading ? 'Creating...' : canCreatePosition ? 'Create Position' : 'Awaiting approval'}</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
